import { graphHooks } from '@tunasong/graph-lib/react'
import { Share } from '@tunasong/icons'
import type { RenderMenuItemProps } from '@tunasong/plugin-lib'
import type { Poll } from '@tunasong/schemas'
import { isPersisted, isPoll } from '@tunasong/schemas'
import {ConfirmDialog, UserList, ListItemIcon, ListItemText, MenuItem as MuiMenuItem} from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useCallback, useMemo, useState } from 'react'
import invariant from 'tiny-invariant'
/** Action to create playlist from songs and folders */
const InviteAction: FC<RenderMenuItemProps> = props => {
  const { element, onClose, ...restProps } = props

  const updateEntity = graphHooks.useEntityUpdate<Poll>()
  const { profile } = graphHooks.useProfile()

  const defaultInvited = isPoll(element) && element.invited ? element.invited : []
  const [invited, setInvited] = useState<string[]>(defaultInvited)

  const [showConfirm, setShowConfirm] = useState(false)
  const handleShowConfirm = useCallback(() => setShowConfirm(true), [])
  const acls = isPersisted(element) ? element.acls : undefined

  const handleCancel = useCallback(
    (ev: React.SyntheticEvent<Element, Event>) => {
      ev.stopPropagation()
      setShowConfirm(false)
      if (onClose) {
        onClose()
      }
    },
    [onClose]
  )

  const handleCreate = useCallback(
    async (ev: React.SyntheticEvent<Element, Event>) => {
      ev.preventDefault()
      invariant(isPersisted(element), 'Element must be persisted')
      updateEntity(element.id, { invited })
    },
    [element, invited, updateEntity]
  )

  const principals = useMemo(
    () => acls?.map(a => a.principal).filter(p => p !== profile?.userId) ?? [],
    [acls, profile]
  )

  if (!(isPoll(element) && isPersisted(element))) {
    return null
  }

  return (
    <MuiMenuItem {...restProps} onClick={handleShowConfirm}>
      <ListItemIcon>
        <Share />
      </ListItemIcon>
      <ListItemText>Invite to poll</ListItemText>
      <ConfirmDialog
        open={showConfirm}
        maxWidth={'sm'}
        fullWidth={true}
        title="Invite to poll"
        text={'Invite users to poll'}
        onCancel={handleCancel}
        onConfirm={handleCreate}
      >
        <UserList userIds={principals} checked={invited} onChange={setInvited} />
      </ConfirmDialog>
    </MuiMenuItem>
  )
}

export default InviteAction
